import React, { useCallback, useMemo, useState } from 'react'

import { Icon, Skeleton } from '@fourthwall/components'
import { ArrowShortDownIcon, ArrowShortUpIcon, TickIcon } from '@fourthwall/icons'
import { useVirtualizer } from '@tanstack/react-virtual'

import { AnalyticsTooltip } from '../AnalyticsTooltip/AnalyticsTooltip'
import * as S from './AnalyticsTable.styled'
import type {
  AnalyticsTableColumn,
  AnalyticsTableDataRow,
  AnalyticsTableProps,
} from './AnalyticsTable.types'
import { AnalyticsTableCell } from './components/AnalyticsTableCell/AnalyticsTableCell'
import type { AnalyticsTableCellValue } from './components/AnalyticsTableCell/AnalyticsTableCell.types'
import { HeadLabelTooltip } from './components/HeadLabelTooltip/HeadLabelTooltip'
import { useScroll } from './hooks/useScroll'

export const AnalyticsTable = ({
  columns,
  data,
  isFirstRowSummary = true,
  isLoading = false,
  activeColumnId,
  emptyMessage = 'No data',
  precision,
  sorting,
  onSortingChange,
  onRowExpand,
}: AnalyticsTableProps) => {
  const visibleColumns = useMemo(() => {
    return columns.filter((column) => !column.hidden)
  }, [columns])

  const { ref, showLeftShadow, showRightShadow, pinnedWidth, updateShadows } =
    useScroll<AnalyticsTableDataRow>(visibleColumns, data)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const toggleRowExpansion = useCallback(
    (rowId: string) => {
      setExpandedRows((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(rowId)) {
          newSet.delete(rowId)
        } else {
          newSet.add(rowId)
        }

        return newSet
      })

      if (onRowExpand) {
        const rowIndex = data.findIndex((row: any) => (row as any).rowId === rowId)
        if (rowIndex !== -1) {
          onRowExpand(rowIndex)
        }
      }
    },
    [onRowExpand, data],
  )

  const isEmpty = data.length === 0
  const hasSummaryRow = isFirstRowSummary && data.length > 0

  const visibleData = useMemo(() => {
    const visibleData: AnalyticsTableDataRow[] = []

    for (const row of data) {
      if (row.rowId !== 'summary-row' && !row.isNested) {
        visibleData.push(row)

        if (row.isExpandable && row.rowId && expandedRows.has(row.rowId) && row.nestedRowsData) {
          row.nestedRowsData.forEach((nestedRow) => {
            visibleData.push(nestedRow)
          })
        }
      }
    }

    return visibleData
  }, [data, expandedRows])

  const virtualDataOffset = 0
  const virtualData = visibleData.slice(virtualDataOffset)

  const rowVirtualizer = useVirtualizer({
    count: virtualData.length,
    getScrollElement: () => ref.current,
    estimateSize: () => 44,
    overscan: 8,
  })

  const renderHeadLabel = (column: AnalyticsTableColumn) => {
    const isSortingActive = sorting?.columnId === column.id

    const label = (
      <S.HeadRowLabel
        underline={!!column.tooltip}
        onClick={onSortingChange ? () => onSortingChange(column.id) : undefined}
      >
        <S.ActiveIcon visible={column.id === activeColumnId}>
          <Icon component={TickIcon} height={8} />
        </S.ActiveIcon>
        {column.label}
        <S.Sorting visible={isSortingActive}>
          <S.SortingIcon isActive={isSortingActive && sorting?.direction === 'asc'}>
            <Icon component={ArrowShortUpIcon} height={7} />
          </S.SortingIcon>
          <S.SortingIcon isActive={isSortingActive && sorting?.direction === 'desc'}>
            <Icon component={ArrowShortDownIcon} height={7} />
          </S.SortingIcon>
        </S.Sorting>
      </S.HeadRowLabel>
    )

    if (column.tooltip) {
      return (
        <S.TooltipWrapper>
          <AnalyticsTooltip content={<HeadLabelTooltip {...column.tooltip} />}>
            {label}
          </AnalyticsTooltip>
        </S.TooltipWrapper>
      )
    }

    return label
  }

  const renderBody = () => {
    if (isLoading) {
      return (
        <>
          {Array.from({ length: 5 }, (_, rowIndex) => (
            <S.Row role="row" key={`r-${rowIndex}`}>
              {visibleColumns.map((column, columnIndex) => {
                return (
                  <S.Td
                    key={`c-${rowIndex}-${String(column.id)}`}
                    role="cell"
                    align={columnIndex === 0 ? 'left' : 'right'}
                    stickyLeft={columnIndex === 0}
                    data-column-index={columnIndex}
                  >
                    <Skeleton>
                      <Skeleton.Shape height={24} width={columnIndex === 0 ? 190 : undefined} />
                    </Skeleton>
                  </S.Td>
                )
              })}
            </S.Row>
          ))}
        </>
      )
    }

    if (isEmpty) {
      return (
        <S.Row role="row">
          <S.EmptyDataCell colSpan={visibleColumns.length}>{emptyMessage}</S.EmptyDataCell>
        </S.Row>
      )
    }

    const virtualItems = rowVirtualizer.getVirtualItems()
    const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0
    const paddingBottom =
      rowVirtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1]?.end ?? 0)

    return (
      <>
        {hasSummaryRow && (
          <S.Row role="row" key="r-0" sticky>
            {visibleColumns.map((column, columnIndex) => {
              const value = (data[0] as Record<string, AnalyticsTableCellValue>)[column.id]

              return (
                <S.Td
                  key={`c-0-${String(column.id)}`}
                  role="cell"
                  align={columnIndex === 0 ? 'left' : 'right'}
                  stickyLeft={columnIndex === 0}
                  data-column-index={columnIndex}
                >
                  <AnalyticsTableCell
                    value={value}
                    type={column.type}
                    decorator={column.decorator}
                    align={columnIndex === 0 ? 'left' : 'right'}
                    columnIndex={columnIndex}
                    precision={precision}
                    isSummary
                  />
                </S.Td>
              )
            })}
          </S.Row>
        )}

        {/* Top spacer */}
        {paddingTop > 0 && (
          <S.Row role="row" aria-hidden key="pad-top" style={{ height: paddingTop }} />
        )}

        {virtualItems.map((virtualRow) => {
          const row = visibleData[virtualRow.index] as any

          return (
            <S.Row role="row" key={`r-${row.rowId || virtualRow.index}`}>
              {visibleColumns.map((column, columnIndex) => {
                const value = (row as Record<string, AnalyticsTableCellValue>)[column.id]

                return (
                  <S.Td
                    key={`c-${row.rowId || virtualRow.index}-${String(column.id)}`}
                    role="cell"
                    align={columnIndex === 0 ? 'left' : 'right'}
                    stickyLeft={columnIndex === 0}
                    data-column-index={columnIndex}
                  >
                    {columnIndex === 0 && row.isExpandable && row.rowId && (
                      <S.ExpandButton
                        onClick={() => toggleRowExpansion(row.rowId)}
                        aria-label={expandedRows.has(row.rowId) ? 'Collapse row' : 'Expand row'}
                        title={expandedRows.has(row.rowId) ? 'Collapse row' : 'Expand row'}
                      >
                        <Icon
                          component={
                            expandedRows.has(row.rowId) ? ArrowShortUpIcon : ArrowShortDownIcon
                          }
                          height={12}
                        />
                      </S.ExpandButton>
                    )}

                    <AnalyticsTableCell
                      value={value}
                      type={column.type}
                      decorator={column.decorator}
                      align={columnIndex === 0 ? 'left' : 'right'}
                      columnIndex={columnIndex}
                      precision={precision}
                      isSummary={false}
                      isNested={row.isNested}
                      nestedLevel={row.nestedLevel}
                      metadata={row.metadata}
                    />
                  </S.Td>
                )
              })}
            </S.Row>
          )
        })}

        {/* Bottom spacer */}
        {paddingBottom > 0 && (
          <S.Row role="row" aria-hidden key="pad-bottom" style={{ height: paddingBottom }} />
        )}
      </>
    )
  }

  return (
    <S.Container>
      <S.TableWrapper id="analytics-table" ref={ref} onScroll={updateShadows}>
        <S.Table role="table" aria-rowcount={data.length} aria-colcount={visibleColumns.length}>
          {/* HEAD */}
          <S.Row role="row" sticky head>
            {visibleColumns.map((column, columnIndex) => (
              <S.Th
                role="columnheader"
                key={`h-${String(column.id)}`}
                align={columnIndex === 0 ? 'left' : 'right'}
                stickyLeft={columnIndex === 0}
                data-column-index={columnIndex}
              >
                {renderHeadLabel(column)}
              </S.Th>
            ))}
          </S.Row>

          {renderBody()}
        </S.Table>
      </S.TableWrapper>

      <S.ShadowsLayer>
        <S.ShadowLeft visible={showLeftShadow} style={{ left: pinnedWidth }} />
        <S.ShadowRight visible={showRightShadow} />
      </S.ShadowsLayer>
    </S.Container>
  )
}
